import { BusinessObjectPart, BusinessObjectPartContext, FeatureFlagsRulesServiceBase, ModelBase, PropertyChangedJoinPoint, Weaver } from '@skylab-shared/core';
import { ClientKind, DocumentClientModel, SaleDocumentKind } from '@skylab-shared/dataModel';
import { propertyChangedJoinPointFilter } from '../../common/property-changed/property-changed-filter';
import { CommunityTransactionForSaleQuery } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { SplitPaymentFlag } from '../sale-feature-flags-rules.service-base';
import { ClientNotSelectedError } from '../violations/client-not-selected-error';
import { ReverseChargeAvailableOnlyForVatPayerWarning } from '../violations/reverse-charge-available-only-for-vat-payer-warning';

interface DocumentModelPart extends ModelBase {
    client: DocumentClientModel;
    communityTransactionId: number;
    kind: SaleDocumentKind;
    taxLiabilityDate: Date;
}

export class SaleDocumentClientBoPart extends BusinessObjectPart<DocumentModelPart> {
    public constructor(
        weaver: Weaver,
        private readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<DocumentModelPart>): Promise<void> {
        await super.initializeAsync(context);
        this.checkIfClientIsSelected();
        this.checkIfSelectedClientIsCorrectOnReverseChargeDocument();
    }

    private checkIfClientIsSelected(): void {
        this.engine.validation.addRule(
            [[this.modelType, 'client'], [this.modelType, 'kind']],
            [[this.modelType, 'client']],
            ctx => {
                const clientRequired = [SaleDocumentKind.Invoice, SaleDocumentKind.VatMarginInvoice, SaleDocumentKind.ProForma, SaleDocumentKind.AdvanceInvoice].includes(ctx.source.kind);
                return !(clientRequired && ctx.source.client === null);
            },
            ClientNotSelectedError
        );
    }

    private checkIfSelectedClientIsCorrectOnReverseChargeDocument(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['communityTransactionId'])(
            () => this.engine.validation.queueValidation([this.modelType, 'client'])
        )));

        this.engine.validation.addRule(
            [[DocumentClientModel, 'kind']],
            [[this.modelType, 'client']],
            ctx => {
                if (!ctx.bo.model.taxLiabilityDate || !this.isSplitPaymentEnabled(ctx.bo.model.taxLiabilityDate))
                    return true;
                const communityTransaction = this.bo.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { id: ctx.bo.model.communityTransactionId });
                if (!ctx.bo.model.client || !communityTransaction)
                    return true;
                if (ctx.bo.model.client.sourceClientId === ctx.source.sourceClientId) {
                    return !(communityTransaction.reverseCharge && ctx.source.kind === ClientKind.Person);
                }
                return true;
            },
            ReverseChargeAvailableOnlyForVatPayerWarning
        );
    }

    private isSplitPaymentEnabled(date: Date) {
        return this.featureFlagsRulesService.getFlagStatus(SplitPaymentFlag, date);
    }
}
